import { Box, Typography, Paper, IconButton, useTheme, alpha, Stack, Avatar } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LuMail, LuPhone, LuExternalLink, LuCopy, LuLifeBuoy } from "react-icons/lu";
import { getOrgTokens } from "../../theme/style/organizationPalette";

const SupportCard = ({ icon: Icon, title, value, subtext, actionLink, isDark, tok, t }) => {
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 4, md: 6 },
        height: '100%',
        flex: 1,
        minHeight: '440px',
        borderRadius: tok.radius.card,
        bgcolor: tok.bg.card,
        border: `1px solid ${tok.border.card}`,
        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          borderColor: "#659d33",
          boxShadow: tok.shadow.cardHover
        }
      }}
    >
      <Avatar
        className="card-icon-bg"
        sx={{
          width: 100,
          height: 100,
          bgcolor: alpha("#659d33", 0.08),
          color: "#659d33",
          mb: 4,
          transition: 'all 0.3s ease',
          border: `1px solid ${alpha("#659d33", 0.1)}`,
        }}
      >
        <Icon size={44} />
      </Avatar>
      
      <Box sx={{ mb: 4, zIndex: 1 }}>
        <Typography sx={{ 
          fontSize: '24px', 
          fontWeight: 800, 
          color: tok.text.primary,
          fontFamily: tok.font.family,
          mb: 1.5,
          letterSpacing: '-0.02em'
        }}>
          {title}
        </Typography>

        <Typography sx={{ 
          fontSize: '15px', 
          color: tok.text.secondary,
          fontFamily: tok.font.family,
          lineHeight: 1.7,
          fontWeight: 400,
          maxWidth: '320px'
        }}>
          {subtext}
        </Typography>
      </Box>

      <Box sx={{ 
        width: '100%',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
        p: 2,
        borderRadius: '12px',
        border: `1px solid ${tok.border.divider}`,
        zIndex: 1
      }}>
        <Typography sx={{ 
          fontWeight: 600, 
          color: tok.text.primary,
          fontFamily: tok.font.family,
          fontSize: '15.5px'
        }}>
          {value}
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <IconButton size="small" onClick={handleCopy} sx={{ color: tok.text.muted, '&:hover': { color: "#659d33" } }}>
            <LuCopy size={18} />
          </IconButton>
          <IconButton 
            size="small" 
            component="a" 
            href={actionLink}
            sx={{ 
                color: "#ffffff", 
                bgcolor: "#659d33", 
                p: 1, 
                borderRadius: '8px',
                '&:hover': { bgcolor: '#4d7c28' }
            }}
          >
            <LuExternalLink size={18} />
          </IconButton>
        </Stack>
      </Box>

      {/* Subtle decorative background element */}
      <Box sx={{
        position: 'absolute',
        top: -20,
        right: -20,
        width: 100,
        height: 100,
        borderRadius: '50%',
        bgcolor: alpha("#659d33", 0.03),
        zIndex: 0
      }} />
    </Paper>
  );
};

const SupportDashboard = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      px: { xs: 2, md: 4 }, 
      py: { xs: 2, md: 3 } 
    }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', animation: 'fadeInUp 0.6s ease-out' }}>
        
        {/* Creative Standalone Hero Header */}
        <Box sx={{
          position: 'relative',
          width: '100%',
          p: { xs: 3, md: 5 },
          borderRadius: tok.radius.card,
          background: isDark 
            ? `linear-gradient(135deg, ${alpha("#659d33", 0.15)} 0%, ${alpha("#000000", 0)} 100%)`
            : `linear-gradient(135deg, ${alpha("#659d33", 0.08)} 0%, ${alpha("#ffffff", 0)} 100%)`,
          border: `1px solid ${alpha("#659d33", 0.1)}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          overflow: 'hidden'
        }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ zIndex: 1 }}>
            <Box sx={{ 
                width: 4, 
                height: 32, 
                bgcolor: "#659d33", 
                borderRadius: '4px',
                boxShadow: `0 0 12px ${alpha("#659d33", 0.4)}`
            }} />
            <Typography sx={{
                fontSize: { xs: '28px', md: '34px' },
                fontWeight: 900,
                color: tok.text.primary,
                fontFamily: tok.font.family,
                letterSpacing: '-0.04em',
                lineHeight: 1
            }}>
                {t("Support Center")}
            </Typography>
          </Stack>
          
          <Typography sx={{
            fontSize: '15.5px',
            color: tok.text.secondary,
            fontFamily: tok.font.family,
            fontWeight: 400,
            pl: 2.5, // Matches the (bar + spacing) indentation of the title exactly
            zIndex: 1,
            maxWidth: '800px',
            lineHeight: 1.6,
            opacity: 0.9,
            textAlign: 'left' // Explicitly left aligned
          }}>
            {t("Get direct access to our expert support team for enterprise environmental guidance and system assistance.")}
          </Typography>
        </Box>

        {/* Contact Grid - High Density Standalone Experience */}
        <Box sx={{ 
          display: 'flex', 
          gap: 4, 
          width: '100%', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'stretch'
        }}>
          <SupportCard 
            tok={tok}
            t={t}
            isDark={isDark}
            icon={LuMail}
            title={t("Email Support")}
            subtext={t("Direct access to our specialists for database inquiries and reporting assistance.")}
            value="support@vrtta.com"
            actionLink="mailto:support@vrtta.com"
          />
          <SupportCard 
            tok={tok}
            t={t}
            isDark={isDark}
            icon={LuPhone}
            title={t("Priority Contact")}
            subtext={t("Immediate telephone assistance for critical system operations and urgent requests.")}
            value="+1 (555) 123-4567"
            actionLink="tel:+15551234567"
          />
        </Box>

      </Box>

      <style jsx="true">{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Box>
  );
};

export default SupportDashboard;
